import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, projectsIn, projectPath } from "@/data/site";
import { EASE } from "@/components/Reveal";

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

const NavItem = ({ item }) => {
  const sub = item.slug ? projectsIn(item.slug) : [];
  return (
    <li className="group relative">
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-link-${slugify(item.label)}`}
        className={({ isActive }) =>
          `relative block max-w-[8.5rem] py-3 text-center font-caps text-[10.5px] leading-snug tracking-[0.1em] uppercase transition-colors duration-300 hover:text-gold-deep xl:text-[11px] xl:tracking-[0.14em] 2xl:max-w-none 2xl:whitespace-nowrap ${
            isActive ? "text-gold-deep" : "text-heritage"
          } after:absolute after:left-0 after:bottom-1 after:h-px after:bg-gold after:transition-[width] after:duration-500 ${
            isActive ? "after:w-full" : "after:w-0 group-hover:after:w-full"
          }`
        }
      >
        {item.label}
      </NavLink>
      {sub.length > 0 && (
        <div className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 translate-y-2 opacity-0 transition-[opacity,transform] duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
          <div className="border border-gold/40 bg-ivory p-5 shadow-[0_24px_60px_-20px_rgba(14,38,31,0.35)]">
            {sub.map((p) => (
              <Link key={p.slug} to={projectPath(p)} data-testid={`nav-sub-${p.slug}`} className="block py-2 font-display text-lg text-heritage transition-colors hover:text-gold-deep">
                {p.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </li>
  );
};

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="fixed inset-0 z-[60] overflow-y-auto bg-heritage text-ivory"
    data-testid="mobile-menu"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: EASE }}
  >
    <div className="flex items-center justify-between px-5 py-4">
      <img src="/images/crest.webp" alt="Estate crest" className="h-12 w-auto" />
      <button onClick={onClose} className="rounded-full border border-gold/50 p-3 text-gold" data-testid="mobile-menu-close-button" aria-label="Close menu">
        <X size={20} />
      </button>
    </div>
    <nav className="px-6 pb-16 pt-4">
      {NAV.map((item, i) => (
        <motion.div key={item.to} className="border-b border-gold/20 py-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05, duration: 0.6, ease: EASE }}>
          <Link to={item.to} data-testid={`mobile-nav-${slugify(item.label)}`} className="font-display text-3xl">{item.label}</Link>
          {item.slug && (
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
              {projectsIn(item.slug).map((p) => (
                <Link key={p.slug} to={projectPath(p)} className="text-sm text-gold-light" data-testid={`mobile-nav-sub-${p.slug}`}>{p.name}</Link>
              ))}
            </div>
          )}
        </motion.div>
      ))}
    </nav>
  </motion.div>
);

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const { pathname } = useLocation();
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setCompact(v > 60));

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
        <div className="relative h-[72px] bg-ivory/95 backdrop-blur-xl lg:h-[88px]">
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-gold/20 via-gold to-gold/20" />
          <div className="mx-auto grid h-full max-w-[1500px] grid-cols-[1fr_auto_1fr] items-center px-4 lg:px-6">
            <nav className="hidden lg:block" aria-label="Main left">
              <ul className="flex items-center justify-start gap-4 xl:gap-7">
                {NAV.slice(0, 5).map((item) => <NavItem key={item.to} item={item} />)}
              </ul>
            </nav>
            <button
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 justify-self-start rounded-full border border-heritage/20 px-4 py-2.5 font-caps text-[11px] tracking-[0.2em] text-heritage lg:hidden"
              data-testid="mobile-menu-open-button"
              aria-label="Open menu"
            >
              <Menu size={16} /> Menu
            </button>
            <div className="w-[120px] lg:w-[300px]" />
            <nav className="hidden lg:block" aria-label="Main right">
              <ul className="flex items-center justify-end gap-4 xl:gap-7">
                {NAV.slice(5).map((item) => <NavItem key={item.to} item={item} />)}
              </ul>
            </nav>
          </div>
          <Link to="/" className="absolute left-1/2 top-0 -translate-x-1/2" data-testid="header-brand-link" aria-label="Little Englishman's Bay Estate, home">
            <motion.div
              className="flex origin-top justify-center rounded-b-[44px] border-x border-b border-gold/60 bg-ivory px-5 pb-2.5 pt-1 shadow-[0_18px_40px_-24px_rgba(14,38,31,0.5)] lg:px-7"
              initial={false}
              animate={{ scale: compact ? 0.6 : 1 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <img
                src="/images/logo.webp"
                alt="Little Englishman's Bay Estate, Est. 1777"
                className="h-[92px] w-auto max-w-none lg:h-[128px]"
              />
            </motion.div>
          </Link>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
